/**
 * usePittRigid.ts
 *
 * React hook that drives the PittRigid ME engine entirely in the browser.
 * Ported from pittrigid.py (Python) / pittRigid.for (Fortran 95).
 *
 * DATABASE LOADING (chunked — ~521 KB per run instead of 23 MB)
 * ──────────────────────────────────────────────────────────────
 * Databases are pre-split by (iClimate × iTrafficPattern × ibaseType) using
 * slice_bins.py.  Only the two chunk files matching the current run are
 * fetched; chunks are cached in memory so repeat runs are free.
 *
 * Place the generated files at:
 *   public/db/coef_crack_c{C}_t{T}_b{B}.bin   shape (408,  16) float64
 *   public/db/coef_fault_c{C}_t{T}_b{B}.bin   shape (9792,  6) float64
 *   public/db/zd0.bin                           shape (50,)     float64
 *
 * Generate with:  python slice_bins.py --bin-dir <dir> --out-dir public/db
 *
 * BUGS FIXED vs. previous version
 * ────────────────────────────────
 * 1. c9 permeability factor removed from faultCalc1 — Java GUI dropped it
 *    (mainJFrame.java line 2326); Python source has no c9.
 * 2. fault_max monthly update now uses x1 (erosion term only), not y1
 *    (erosion × moisture).  Matches pittrigid.py line 480.
 * 3. refTraf is unconditionally 10 000 × 0.95 — the iDowel conditional
 *    was removed in the validated Python (comment on line 464-465).
 * 4. Prediction-mode iThick uses Math.trunc(), not Math.round() — mirrors
 *    Python int() / Fortran INT() truncation semantics.
 * 5. Dowel search init: both traffic branches now start at resultIDowel = -1
 *    so the first increment always lands on 0 (undoweled), matching Python.
 * 6. iThick1 cap changed to NPCC - 1 (= 16) — NPCC = 17 is the count;
 *    valid indices are 0–16, so capping at 17 is out-of-bounds.
 */

import { useState, useCallback } from 'react';

// ─── Dimension constants (mirror Fortran COMMON /dim1/) ────────────────────────
const NSH      = 2;
const NALP     = 3;
const NW       = 2;
const NJ       = 2;
const NPCC     = 17;
const NDOWEL   = 4;
const NSTRENGTH = 6;

// ─── Chunk dimensions (rows per chunk file) ────────────────────────────────────
/** Rows per crack chunk: NSH × NALP × NW × NJ × NPCC = 408 */
const CRACK_INNER = NSH * NALP * NW * NJ * NPCC;          // 408
/** Rows per fault chunk: NSH × NALP × NW × NJ × NDOWEL × NSTRENGTH × NPCC = 9792 */
const FAULT_INNER = NSH * NALP * NW * NJ * NDOWEL * NSTRENGTH * NPCC; // 9792

// ─── Chunk cache ───────────────────────────────────────────────────────────────
interface Chunk {
  crackFlat: Float64Array;   // CRACK_INNER × 16
  faultFlat: Float64Array;   // FAULT_INNER × 6
  zd0:       Float64Array;   // 50
}
const _chunkCache = new Map<string, Chunk>();
let   _zd0Cache: Float64Array | null = null;

const DB_BASE = '/data/pittrigid';

async function fetchF64(url: string): Promise<Float64Array> {
  const res = await fetch(url);
  if (!res.ok) {
    let preview = '';
    try { preview = (await res.text()).slice(0, 120).replace(/\s+/g, ' '); } catch { /* ignore */ }
    throw new Error(
      `DB fetch failed: ${url} — HTTP ${res.status} ${res.statusText}` +
      (preview ? `\nBody: ${preview}` : '')
    );
  }
  const buf = await res.arrayBuffer();
  if (buf.byteLength % 8 !== 0) {
    throw new Error(
      `DB file not aligned: ${url}\n` +
      `  Got ${buf.byteLength} bytes (not a multiple of 8).\n` +
      `  Expected sizes — crack: ${CRACK_INNER * 16 * 8} B, fault: ${FAULT_INNER * 6 * 8} B, zd0: 400 B.\n` +
      `  Check that the file exists at that path and was generated by slice_bins.py.\n` +
      `  DB_BASE = "${DB_BASE}"`
    );
  }
  return new Float64Array(buf);
}

async function loadChunk(iC: number, iT: number, iB: number): Promise<Chunk> {
  const key = `c${iC}_t${iT}_b${iB}`;
  if (_chunkCache.has(key)) return _chunkCache.get(key)!;

  const [crackFlat, faultFlat, zd0] = await Promise.all([
    fetchF64(`${DB_BASE}/coef_crack_c${iC}_t${iT}_b${iB}.bin`),
    fetchF64(`${DB_BASE}/coef_fault_c${iC}_t${iT}_b${iB}.bin`),
    _zd0Cache ? Promise.resolve(_zd0Cache) : fetchF64(`${DB_BASE}/zd0.bin`),
  ]);

  const expectedCrack = CRACK_INNER * 16;
  const expectedFault = FAULT_INNER * 6;
  if (crackFlat.length !== expectedCrack)
    throw new Error(`crack chunk wrong row count (key=${key}): got ${crackFlat.length}, expected ${expectedCrack}`);
  if (faultFlat.length !== expectedFault)
    throw new Error(`fault chunk wrong row count (key=${key}): got ${faultFlat.length}, expected ${expectedFault}`);
  if (zd0.length !== 50)
    throw new Error(`zd0 wrong size: got ${zd0.length}, expected 50`);

  _zd0Cache = zd0;
  const chunk: Chunk = { crackFlat, faultFlat, zd0 };
  _chunkCache.set(key, chunk);
  return chunk;
}

export const DOWEL_DIAMETERS = [0.0, 1.0, 1.25, 1.5] as const;

// ─── Lane distribution factors (mirrors Java GUI mainJFrame lines 1858-1876) ──
export const LANE_FACTORS: Record<number, number> = { 0: 1.0, 1: 0.9, 2: 0.8, 3: 0.6 };

// ─── Preset calibration coefficients ──────────────────────────────────────────
export type ModelCoeffs = {
  crackC1d: number; crackC2d: number;
  crackC1R: number; crackC2R: number; crackC3R: number;
  c1Fault: number; c2Fault: number; c3Fault: number; c4Fault: number;
  c5Fault: number; c6Fault: number; c7Fault: number;
  faultC1R: number; faultC2R: number; faultC3R: number;
};

// PresetKey defined as a plain literal union — no reference to PRESETS, avoiding circularity
export type PresetKey = 'national' | 'penndot_new' | 'penndot_paveme' | 'custom';

export const PRESETS: Record<Exclude<PresetKey, 'custom'>, ModelCoeffs> = {
  national: {
    crackC1d: 0.52,  crackC2d: -2.17,
    crackC1R: 3.5522, crackC2R: 0.3415, crackC3R: 0.75,
    c1Fault: 0.595, c2Fault: 1.636, c3Fault: 0.00217, c4Fault: 0.00444,
    c5Fault: 250.0, c6Fault: 0.47,  c7Fault: 7.3,
    faultC1R: 0.07162, faultC2R: 0.368, faultC3R: 0.00806,
  },
  penndot_new: {
    crackC1d: 0.47,  crackC2d: -2.05,
    crackC1R: 3.1306, crackC2R: 0.3582, crackC3R: 0.5,
    c1Fault: 0.595, c2Fault: 1.1, c3Fault: 0.0035, c4Fault: 0.015,
    c5Fault: 250.0, c6Fault: 0.4, c7Fault: 2.0,
    faultC1R: 0.07602, faultC2R: 0.35027, faultC3R: 0.0,
  },
  penndot_paveme: {
    crackC1d: 0.47,  crackC2d: -2.05,
    crackC1R: 3.1306, crackC2R: 0.3582, crackC3R: 0.5,
    c1Fault: 0.595, c2Fault: 1.636, c3Fault: 0.00147, c4Fault: 0.00444,
    c5Fault: 250.0, c6Fault: 0.4,   c7Fault: 7.3,
    faultC1R: 0.08162, faultC2R: 0.3481, faultC3R: 0.008,
  },
};

// ─── Input / Output types ──────────────────────────────────────────────────────

export interface PittRigidInputs {
  // Environment
  iClimate: number;         // 0=Erie 1=Pittsburgh 2=Altoona 3=Williamsport 4=Philadelphia
  iLife: number;            // design life, years

  // Traffic (traf0 is the design-lane-adjusted AADTT, after lane factor applied)
  traf0: number;
  rate: number;             // annual growth rate, %
  iTrafficPattern: number;  // 0=Urban Interstate 1=Urban Arterial 2=Rural

  // Structure
  hPCC: number;             // slab thickness, inches
  iDowel: number;           // 0=none 1=1.0in 2=1.25in 3=1.5in
  jpointspace: number;      // 0=12ft 1=15ft
  iwidth: number;           // 0=12ft lane 1=14ft lane
  ishoulder: number;        // 0=tied PCC 1=asphalt/other
  ialpPCC: number;          // 0=4.5 1=5.0 2=5.5 (×10⁻⁶/°F)
  ibaseType: number;        // 0=aggregate 1=CTB 2=lean concrete
  pccMR28: number;          // modulus of rupture, psi

  // Mode
  iRel: number;             // 0=prediction 1=design

  // Reliability
  relCrack: number;         // % (50–99)
  relFault: number;         // % (50–99)

  // Design targets (iRel=1 only)
  targetSlabs: number;      // % cracked slabs
  targetFault: number;      // inches

  // Cracking model coefficients
  crackC1d: number; crackC2d: number;
  crackC1R: number; crackC2R: number; crackC3R: number;

  // Faulting model coefficients
  c1Fault: number; c2Fault: number; c3Fault: number; c4Fault: number;
  c5Fault: number; c6Fault: number; c7Fault: number;
  faultC1R: number; faultC2R: number; faultC3R: number;
}

export interface PittRigidResults {
  hPCC: number;
  dowelDiameter: number;
  iDowel: number;
  crackAge: number[];       // % cracked slabs, 50% reliability, length=iLife
  crackAgeRel: number[];    // % cracked slabs, target reliability, length=iLife
  fault: number[];          // faulting inches, 50% reliability, length=iLife
  faultRel: number[];       // faulting inches, target reliability, length=iLife
}

// ─── Hook status ──────────────────────────────────────────────────────────────

export type HookStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'running' }
  | { kind: 'done' }
  | { kind: 'error'; message: string };

// ─── Default inputs ────────────────────────────────────────────────────────────

export const DEFAULT_INPUTS: PittRigidInputs = {
  iClimate: 0, iLife: 20,
  traf0: 10000, rate: 3.0, iTrafficPattern: 0,
  hPCC: 6.8, iDowel: 0, jpointspace: 1, iwidth: 0,
  ishoulder: 0, ialpPCC: 0, ibaseType: 0, pccMR28: 650,
  iRel: 1,
  relCrack: 90, relFault: 90,
  targetSlabs: 10, targetFault: 0.15,
  ...PRESETS.national,
};

// ─── Pure engine functions (mirrors Python pittrigid.py) ──────────────────────

/** Log-linear interpolation if both endpoints positive, else linear (Fortran dLInter) */
function dlInter(x: number, v1: number, v2: number, x1: number, x2: number): number {
  if (v1 > 0 && v2 > 0) {
    const logV = (Math.log(v1) * (x2 - x) + Math.log(v2) * (x - x1)) / (x2 - x1);
    return Math.exp(logV);
  }
  const result = (v1 * (x2 - x) + v2 * (x - x1)) / (x2 - x1);
  return Math.max(0, result);
}

/**
 * Row index within the crack chunk (outer axes stripped).
 * Global formula minus ibaseType/iClimate/iTrafficPattern terms.
 * Mirrors _crack_db_idx in pittrigid.py with ibaseType=0,iClimate=0,iTrafficPattern=0.
 */
function crackDbIdx(inp: PittRigidInputs, iThick: number): number {
  return (
    inp.ishoulder
    + NSH   * inp.ialpPCC
    + NSH   * NALP * inp.iwidth
    + NSH   * NALP * NW   * inp.jpointspace
    + NSH   * NALP * NW   * NJ    * iThick
  );
}

/**
 * Row index within the fault chunk (outer axes stripped).
 * Global formula minus ibaseType/iClimate/iTrafficPattern terms.
 * Mirrors _fault_db_idx in pittrigid.py with ibaseType=0,iClimate=0,iTrafficPattern=0.
 */
function faultDbIdx(inp: PittRigidInputs, iThick1: number, iStrength: number): number {
  return (
    inp.ishoulder
    + NSH    * inp.ialpPCC
    + NSH    * NALP   * inp.iwidth
    + NSH    * NALP   * NW     * inp.jpointspace
    + NSH    * NALP   * NW     * NJ      * inp.iDowel
    + NSH    * NALP   * NW     * NJ      * NDOWEL   * iStrength
    + NSH    * NALP   * NW     * NJ      * NDOWEL   * NSTRENGTH * iThick1
  );
}

// Wet days per region (main program uses slightly different values than faultCalc1)
const WET_DAYS_FAULT: Record<number, number> = {
  0: 204.64, 1: 192.01, 2: 192.24, 3: 179.44, 4: 158.70,
};

/**
 * Damage calc (Fortran damCalc).
 * Returns [damBU, damTD], each length iLife+2 (index 0 unused, index 1 = 0.0).
 */
function damCalc(
  inp: PittRigidInputs,
  iThick0: number, ih: number,
  chunk: Chunk,
): [number[], number[]] {
  const iThick = iThick0 + ih;
  const idx = crackDbIdx(inp, iThick);
  const base = idx * 16;
  const c = (i: number) => chunk.crackFlat[base + i];
  const mr2 = inp.pccMR28 / 650.0;

  const c1  = Math.exp(c(0)  + c(1)  * mr2 + c(2)  * mr2 * mr2);
  const c1T = Math.exp(c(8)  + c(9)  * mr2 + c(10) * mr2 * mr2);

  const damBU: number[] = new Array(inp.iLife + 2).fill(0);
  const damTD: number[] = new Array(inp.iLife + 2).fill(0);

  for (let i = 1; i <= inp.iLife; i++) {
    const fi = i;
    const logISq = Math.log(fi) ** 2;
    const trafA = inp.traf0 * (1 + inp.rate / 100) ** (i - 1) / (2000 * 0.95);

    const c2  = fi ** (c(3)  + c(4)  * mr2);
    const c3  = Math.exp((c(5)  + c(6)  * mr2 + c(7)  * mr2 * mr2) * logISq);
    damBU[i + 1] = damBU[i] + c1  * c2  * c3  * trafA;

    const c2T = fi ** (c(11) + c(12) * mr2);
    const c3T = Math.exp((c(13) + c(14) * mr2 + c(15) * mr2 * mr2) * logISq);
    damTD[i + 1] = damTD[i] + c1T * c2T * c3T * trafA;
  }
  return [damBU, damTD];
}

/**
 * Cracking calc (Fortran crackCalc).
 * Returns [crackAge, crackAgeRel], each length iLife+1 (index 0 unused).
 */
function crackCalc(
  inp: PittRigidInputs,
  iThick: number, zd: number,
  chunk: Chunk,
): [number[], number[]] {
  const hpcc0 = iThick / 2.0 + 6.0;
  const [db0bu, db0td] = damCalc(inp, iThick, 0, chunk);
  const [db1bu, db1td] = damCalc(inp, iThick, 1, chunk);

  const crackAge:    number[] = new Array(inp.iLife + 1).fill(0);
  const crackAgeRel: number[] = new Array(inp.iLife + 1).fill(0);

  for (let age = 1; age <= inp.iLife; age++) {
    const damBU1 = dlInter(inp.hPCC, db0bu[age + 1], db1bu[age + 1], hpcc0, hpcc0 + 0.5);
    const damTD1 = dlInter(inp.hPCC, db0td[age + 1], db1td[age + 1], hpcc0, hpcc0 + 0.5);

    const crackBU = damBU1 > 0 ? 1 / (1 + inp.crackC1d * damBU1 ** inp.crackC2d) : 0;
    const crackTD = damTD1 > 0 ? 1 / (1 + inp.crackC1d * damTD1 ** inp.crackC2d) : 0;

    const pct = 100 * (crackBU + crackTD - crackBU * crackTD);
    crackAge[age] = pct;

    const sd = inp.crackC1R * pct ** inp.crackC2R + inp.crackC3R;
    crackAgeRel[age] = Math.min(pct + zd * sd, 100);
  }
  return [crackAge, crackAgeRel];
}

/**
 * Faulting inner loop (Fortran faultCalc1).
 * Returns [de1, fault1], each length iLife+2 (index 0 unused).
 *
 * Fixes vs. previous version:
 *   - c9 permeability factor removed (Java GUI dropped it, Python has no c9)
 *   - iThick1 capped at NPCC-1 (=16), not NPCC (=17 which is out-of-bounds)
 *   - refTraf unconditionally 10000*0.95 (iDowel conditional was removed)
 *   - fault_max monthly update uses x1 only, not x1*log_term (matches Python line 480)
 */
function faultCalc1(
  inp: PittRigidInputs,
  iThick: number, ih: number,
  chunk: Chunk,
): [number[], number[]] {
  const mr28 = inp.pccMR28;
  let iStrength: number;
  if      (mr28 < 550) iStrength = 0;
  else if (mr28 < 625) iStrength = 1;
  else if (mr28 < 675) iStrength = 2;
  else if (mr28 < 750) iStrength = 3;
  else if (mr28 < 850) iStrength = 4;
  else                 iStrength = 5;

  // Cap at NPCC-1 (=16): NPCC=17 is the COUNT, valid indices are 0..16.
  // min(iThick+ih, NPCC) in Python can produce 17 which is OOB; use 16.
  const iThick1 = Math.min(iThick + ih, NPCC - 1);

  // c9 (permeability factor) intentionally omitted — removed from Java GUI
  // (mainJFrame.java line 2326); Python source does not use it.
  let erod: number, hbase: number, gambase: number;
  if (inp.ibaseType === 0) {
    erod = 3; hbase = 6; gambase = 127.2;   // aggregate
  } else if (inp.ibaseType === 1) {
    erod = 1; hbase = 4; gambase = 150.0;   // CTB
  } else {
    erod = 2; hbase = 4; gambase = 135.0;   // lean concrete
  }

  const wetDays = WET_DAYS_FAULT[inp.iClimate] ?? 158.70;
  const p200 = 63.2;
  const hpccF = iThick1 * 0.5 + 6.0;
  const ps = 144.0 * (hpccF * 150.0 + hbase * gambase) / 1728.0;

  const idx = faultDbIdx(inp, iThick1, iStrength);
  const base = idx * 6;
  const cf = (i: number) => chunk.faultFlat[base + i];

  // x1 = erosion term (used alone in the monthly fault_max update)
  // logTerm = moisture term (p200 * wetDays / ps, no c9)
  const x1      = Math.log10(1 + inp.c5Fault * 5 ** erod);
  const logTerm = Math.log10(p200 * wetDays / ps);

  const c12Def  = inp.c1Fault + inp.c2Fault * cf(3) ** 0.25;
  const fDelta  = cf(2) / (c12Def * (x1 * logTerm) ** 0.4);
  const c12     = inp.c1Fault + inp.c2Fault * cf(3) ** 0.25;
  const c34     = inp.c3Fault + inp.c4Fault * cf(3) ** 0.25;
  let faultMax  = c12 * fDelta * (x1 * logTerm) ** inp.c6Fault;

  // refTraf is unconditional — the iDowel-conditional version was removed
  // in the validated Python port (pittrigid.py lines 464-465 comment).
  const refTraf = 10000 * 0.95;

  let ageEff = 0;
  const de1:    number[] = new Array(inp.iLife + 2).fill(0);
  const fault1: number[] = new Array(inp.iLife + 2).fill(0);

  for (let i = 1; i <= inp.iLife; i++) {
    ageEff += (inp.traf0 / refTraf) * (1 + inp.rate / 100) ** (i - 1);

    const deVal = cf(1) * ageEff + cf(0) * ageEff * ageEff;
    de1[i + 1] = Math.max(0, deVal);

    const deMonth = (de1[i + 1] - de1[i]) / 12.0;
    let fault1C = fault1[i];

    if (fault1C < 10) {
      for (let _j = 0; _j < 12; _j++) {
        const dFault = (fault1C - faultMax) ** 2 * c34 * deMonth;
        fault1C += dFault;
        // fault_max update uses x1 alone (erosion term), NOT x1*logTerm.
        // Matches pittrigid.py line 480: c7Fault * x1**c6Fault / 1_000_000
        faultMax += deMonth * inp.c7Fault * x1 ** inp.c6Fault / 1_000_000;
      }
    }
    fault1[i + 1] = fault1C;
  }
  return [de1, fault1];
}

/**
 * Faulting outer wrapper (Fortran faultCalc).
 * Returns [fault, faultRel], each length iLife+1 (index 0 unused).
 */
function faultCalc(
  inp: PittRigidInputs,
  iThick: number, zd: number,
  chunk: Chunk,
): [number[], number[]] {
  const hpcc0 = iThick / 2.0 + 6.0;
  const [, fault10] = faultCalc1(inp, iThick, 0, chunk);
  const [, fault11] = faultCalc1(inp, iThick, 1, chunk);

  const fault:    number[] = new Array(inp.iLife + 1).fill(0);
  const faultRel: number[] = new Array(inp.iLife + 1).fill(0);

  for (let age = 1; age <= inp.iLife; age++) {
    fault[age] = dlInter(inp.hPCC, fault10[age + 1], fault11[age + 1], hpcc0, hpcc0 + 0.5);
    const sd = inp.faultC1R * fault[age] ** inp.faultC2R + inp.faultC3R;
    faultRel[age] = fault[age] + zd * sd;
  }
  return [fault, faultRel];
}

/**
 * Main engine (Fortran main program logic).
 */
function runEngine(
  inp: PittRigidInputs,
  chunk: Chunk,
): PittRigidResults {
  const { zd0 } = chunk;
  const zdC = zd0[Math.round(inp.relCrack) - 50];
  const zdF = zd0[Math.round(inp.relFault) - 50];
  const hpccMax = 14.0;
  const iL = inp.iLife;

  let crackAge: number[], crackAgeRel: number[];
  let fault: number[], faultRel: number[];
  let resultHPCC: number, resultIDowel: number;

  if (inp.iRel === 0) {
    // ── Prediction mode ──────────────────────────────────────────────────────
    // int() in Python / INT() in Fortran truncates toward zero — use Math.trunc,
    // not Math.round.  e.g. hPCC=7.9: trunc((7.9-6)*2)=3, round=4 → wrong row.
    const iThick = Math.trunc((inp.hPCC - 6.0) * 2.0);
    [crackAge, crackAgeRel] = crackCalc(inp, iThick, zdC, chunk);
    [fault, faultRel]   = faultCalc(inp, iThick, zdF, chunk);
    resultHPCC   = inp.hPCC;
    resultIDowel = inp.iDowel;
  } else {
    // ── Design mode ──────────────────────────────────────────────────────────
    const w = { ...inp };

    // Coarse 0.5-in search
    crackAgeRel = new Array(iL + 1).fill(100);
    crackAge    = new Array(iL + 1).fill(0);
    let iTh = -1;
    while (iTh < NPCC && crackAgeRel[iL] > inp.targetSlabs) {
      iTh++;
      w.hPCC = iTh / 2.0 + 6.0;
      [crackAge, crackAgeRel] = crackCalc(w, iTh, zdC, chunk);
    }

    // Refine to 0.01-in
    if (iTh === 0) {
      w.hPCC = 6.0;
    } else if (iTh >= NPCC) {
      w.hPCC = hpccMax;
    } else {
      const hpcc0 = (iTh - 1) / 2.0 + 6.0;
      const dh = 0.01;
      w.hPCC = hpcc0 - dh;
      crackAgeRel = new Array(iL + 1).fill(100);
      while (w.hPCC < hpccMax && crackAgeRel[iL] > inp.targetSlabs) {
        w.hPCC = parseFloat((w.hPCC + dh).toFixed(4));
        const iThickFine = Math.round((w.hPCC - 6.0) * 2.0);
        [crackAge, crackAgeRel] = crackCalc(w, iThickFine, zdC, chunk);
      }
    }

    resultHPCC = w.hPCC;
    const iFinal = Math.round((resultHPCC - 6.0) * 2.0);
    [crackAge, crackAgeRel] = crackCalc(w, iFinal, zdC, chunk);

    // Dowel search — both traffic branches start at resultIDowel = -1 so the
    // first increment always reaches 0 (undoweled check is never skipped).
    // This mirrors Python exactly: the high-traffic branch sets 0 then -= 1.
    resultIDowel = -1;
    faultRel = new Array(iL + 1).fill(inp.targetFault + 1);
    fault    = new Array(iL + 1).fill(0);

    while (faultRel[iL] > inp.targetFault && resultIDowel < NDOWEL - 1) {
      resultIDowel++;
      w.iDowel = resultIDowel;
      [fault, faultRel] = faultCalc(w, iFinal, zdF, chunk);
    }
    if (resultIDowel >= NDOWEL) resultIDowel = NDOWEL - 1;

    // Final fault pass with resolved dowel
    w.iDowel = resultIDowel;
    [fault, faultRel] = faultCalc(w, iFinal, zdF, chunk);
  }

  return {
    hPCC: resultHPCC,
    dowelDiameter: DOWEL_DIAMETERS[Math.max(resultIDowel, 0)],
    iDowel: resultIDowel,
    crackAge:    crackAge!.slice(1, iL + 1),
    crackAgeRel: crackAgeRel!.slice(1, iL + 1),
    fault:    fault!.slice(1, iL + 1),
    faultRel: faultRel!.slice(1, iL + 1),
  };
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function usePittRigid() {
  const [status, setStatus] = useState<HookStatus>({ kind: 'ready' });
  const [results, setResults] = useState<PittRigidResults | null>(null);

  const run = useCallback(async (inp: PittRigidInputs) => {
    setStatus({ kind: 'loading' });
    setResults(null);

    try {
      // Fetch (or hit memory cache for) the chunk pair for this run's
      // (iClimate × iTrafficPattern × ibaseType) combination.
      const chunk = await loadChunk(inp.iClimate, inp.iTrafficPattern, inp.ibaseType);

      setStatus({ kind: 'running' });

      // Yield one frame so the UI can paint "Running…" before the
      // synchronous engine work begins.
      await new Promise<void>(resolve => setTimeout(resolve, 0));

      const res = runEngine(inp, chunk);
      setResults(res);
      setStatus({ kind: 'done' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus({ kind: 'error', message: msg });
    }
  }, []);

  const clearResults = useCallback(() => {
    setResults(null);
    setStatus(s => s.kind === 'done' || s.kind === 'error' ? { kind: 'ready' } : s);
  }, []);

  return { status, results, run, clearResults };
}